import React, { useEffect } from 'react';
import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  MUIDataTable,
  MUIDataTableColumn,
  SearchBar,
  SectionHeader,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
} from 'web-library';
import { RouteComponentProps, useHistory } from 'react-router-dom';

import RouteKey from '../../../routes/key';
import { usePostCandidateListing } from '../../../api-refactored/transaction/usePostCandidateListing';

const useStyles = makeStyles()((theme) =>
({
  '@global': {
    '.MuiTableCell-root': {
      verticalAlign: 'middle',
    },
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  button: {
    padding: '0px !important',
  },
}));

const { coordinator, viewDetails } = RouteKey.transaction.child;

interface DataTableParams {
  state: TableState;
  classes: Record<string,string>;
  dispatch: React.Dispatch<TableActionTypes>;
  t: TFunction<'translation'>;
  history: RouteComponentProps['history'];
}

const useTableColumns = (params: DataTableParams): MUIDataTableColumn[] => [
  {
    name: 'finNric',
    label: params.t('transaction->>Coordinator.Table.Column.Id'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2" className={params.classes.tableText}>
          {params.state.data[dataIndex].finNric}
        </Typography>
      ),
    },
  },
  {
    name: 'candidateName',
    label: params.t('transaction->>Coordinator.Table.Column.Name'),
    options: {
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2" className={params.classes.tableText}>
          {params.state.data[dataIndex].candidateName}
        </Typography>
      ),
    },
  },
  {
    name: 'examLevel',
    label: params.t('transaction->>Coordinator.Table.Column.ExamLevel'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2" className={params.classes.tableText}>
          {params.state.data[dataIndex].examLevel}
        </Typography>
      ),
    },
  },
  {
    name: 'actions',
    label: params.t('transaction->>Coordinator.Table.Column.Actions'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Box>
          <Button
            className={params.classes.button}
            color="primary"
            variant="text"
            size="medium"
            onClick={() => {
              params.history.push(
                `${coordinator.route}/${params.state.data[dataIndex].uuid}/${viewDetails.route}`
              );
            }}
          >
            {params.t('transaction->>Coordinator.Table.Column.ViewDetails')}
          </Button>
        </Box>
      ),
    },
  },
];

const CandidateListingTable = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable(undefined, true);

  const tableColumns = useTableColumns({
    state,
    classes,
    dispatch: tableDispatch,
    t,
    history,
  });

  const { mutate, isLoading } = usePostCandidateListing({
    onSuccess: (data) =>
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes),
  });

  useEffect(() => {
    mutate({
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
      },
      query: state.search ?? '',
    });
  }, [mutate, state.order, state.page, state.size, state.search, state.sortColumn]);

  return (
    <>
      <SectionHeader title={t('transaction->>Page.SubHeader.CandidateListing')} />
      <Box className={classes.searchContainer}>
        <SearchBar
          placeholder={t('transaction->>Coordinator.Table.SearchBar')}
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue={state.search}
          onSearch={(str) => {
            tableDispatch({
              type: 'UPDATE_SEARCH',
              payload: str,
            });
          }}
        />
      </Box>
      <MUIDataTable
        state={state}
        dispatch={tableDispatch}
        columns={tableColumns}
        data={state.data}
        isLoading={isLoading}
        emptyDataLabel={t('transaction->>Coordinator.Table.EmptyLabel')}
        options={{
          ...tOptions,
          filter: false,
          sort: true,
          pagination: true,
        }}
      />
    </>
  );
};

export default CandidateListingTable;
